import { useAuth } from '../contexts/AuthContext'
import { usePageTitle } from '../hooks/usePageTitle'
import { useShares } from '../hooks/useShares'
import { useWallet } from '../hooks/useWallet'
import { formatCents } from '../lib/clubMoney'
import {
  ClubButton,
  ClubCard,
  ClubPage,
  Container,
  EmptyPlate,
  PageHead,
  SectionHead,
  SignInPrompt,
} from '../components/golf'
import type { OutingShare } from '../lib/types'

/**
 * The club's wallet (`/clubhouse/wallet`, design card `the-wallet`): what the
 * club holds for this member, and what they owe on rounds somebody else
 * fronted.
 *
 * NOT `/steam/wallet`. That is a deliberately frozen legacy surface with live
 * Stripe money in it (docs/fireside/steam-legacy.md), on the disjoint firelight
 * kit. This screen imports only from `components/golf/` and never links there.
 *
 * The booking email sends invitees straight here, so the signed-out arrival is
 * a first-class state rather than an error — a guest gets the club's own
 * sign-in prompt, not a blank page.
 */

function formatTeeTime(iso: string | null): string {
  if (!iso) return 'Date to be posted'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  const day = new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(date)
  const time = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
  return `${day} · ${time}`
}

/**
 * One share. Settled rows stay on the page rather than vanishing — a member
 * who just paid needs to see that it landed, and the record of having paid is
 * the point of a ledger.
 */
function ShareCard({
  share,
  onPay,
  paying,
  covered,
}: {
  share: OutingShare
  onPay: () => void
  paying: boolean
  covered: boolean
}) {
  const settled = share.outstanding_cents === 0

  return (
    <ClubCard className="p-5.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
        <div>
          <p className="font-display text-[19px] text-pine">
            {share.course_name ?? 'A round'}
          </p>
          <p className="mt-0.5 text-[13.5px] text-ink-soft">
            {formatTeeTime(share.tee_off_at)}
            {share.holes ? ` · ${share.holes} holes` : ''}
          </p>
        </div>
        <div className="text-right">
          <span className="block text-[10.5px] uppercase tracking-[0.14em] text-brass-deep">
            Your share
          </span>
          <span className="font-display text-[25px] tabular-nums text-pine">
            {formatCents(share.share_cents ?? 0)}
          </span>
        </div>
      </div>

      <p className="mt-2.5 text-[12.5px] text-ink-soft">
        Arranged by {share.organizer_name ?? 'a member'}
        {share.booking_ref ? ` · course reference ${share.booking_ref}` : ''}
      </p>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-brass/30 pt-4">
        {settled ? (
          <span className="rounded-club-sm border border-fairway/40 bg-sage-soft/60 px-2 py-0.5 text-[10.5px] uppercase tracking-[0.1em] text-fairway">
            Settled
          </span>
        ) : (
          <span className="rounded-club-sm border border-brass/50 bg-sage-soft/40 px-2 py-0.5 text-[10.5px] uppercase tracking-[0.1em] text-brass-deep">
            {share.paid_cents > 0
              ? `${formatCents(share.outstanding_cents)} still owed`
              : 'Booked · unsettled'}
          </span>
        )}

        {settled ? (
          <span className="text-[13px] text-ink-soft">Paid in full</span>
        ) : covered ? (
          <ClubButton onClick={onPay} disabled={paying}>
            {paying ? 'Paying…' : `Pay ${formatCents(share.outstanding_cents)}`}
          </ClubButton>
        ) : (
          // Refusing here rather than at the server keeps the member out of a
          // failure they can predict: the balance is on screen beside this.
          <span className="text-[13px] text-ink-soft">
            Add funds to settle this
          </span>
        )}
      </div>
    </ClubCard>
  )
}

export function ClubWalletScreen() {
  usePageTitle('The Wallet')
  const { isAuthenticated, features } = useAuth()
  const walletEnabled = features.group_wallet && isAuthenticated
  const { wallet, isLoading: walletLoading } = useWallet({ enabled: walletEnabled })
  const { shares, isLoading, error, pay, payingId, payError, paidWallet } = useShares({
    enabled: walletEnabled,
  })

  // The server returns the payer's recomputed wallet with every settlement, so
  // the balance on screen is the ledger's, not an optimistic subtraction.
  const balanceCents = paidWallet?.balance_cents ?? wallet?.balance_cents ?? 0
  const owed = shares.filter((share) => share.outstanding_cents > 0)

  return (
    <ClubPage chrome="clubhouse">
      <PageHead
        eyebrow="Clubhouse"
        title="The"
        emphasis="Wallet"
        lede="What the club holds for you, and what you owe on rounds somebody else fronted. Settling a share sends it straight to the member who paid the course."
      />

      <Container className="pb-20">
        {!isAuthenticated ? (
          <SignInPrompt what="your balance and anything you owe" />
        ) : !features.group_wallet ? (
          <EmptyPlate
            title="The wallet isn't open yet."
            description="The club isn't holding money for members on this site yet. When it is, your balance and any shares you owe will be here."
          />
        ) : (
          <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]">
            <div>
              <SectionHead eyebrow="What you owe" title="Your shares" />

              {isLoading ? (
                <p className="text-[14.5px] text-ink-soft">Reading the book…</p>
              ) : error ? (
                <EmptyPlate
                  title="That didn't load."
                  description={error}
                />
              ) : shares.length === 0 ? (
                <EmptyPlate
                  title="Nothing outstanding."
                  description="You're square with everyone. Shares appear here once an outing you accepted has actually been booked — not before."
                />
              ) : (
                <div className="flex flex-col gap-3.5">
                  {shares.map((share) => (
                    <ShareCard
                      key={share.outing_id}
                      share={share}
                      paying={payingId === share.outing_id}
                      covered={balanceCents >= share.outstanding_cents}
                      onPay={() => void pay(share.outing_id)}
                    />
                  ))}
                </div>
              )}

              {payError && (
                <p role="alert" className="mt-4 text-[12.5px] text-flag">
                  {payError}
                </p>
              )}
            </div>

            <aside>
              <ClubCard inverted className="p-5.5">
                <span className="block text-[10.5px] uppercase tracking-[0.14em] text-gilt">
                  In your wallet
                </span>
                <p className="mt-1 font-display text-[34px] leading-none tabular-nums text-paper">
                  {walletLoading && !wallet ? '—' : formatCents(balanceCents)}
                </p>
                <p className="mt-2 text-[12.5px] text-sage">
                  Held by the club. Yours to spend or withdraw.
                </p>
                {owed.length > 0 && (
                  <p className="mt-3 text-[12.5px] text-sage">
                    {formatCents(owed.reduce((sum, share) => sum + share.outstanding_cents, 0))}{' '}
                    owed across {owed.length} {owed.length === 1 ? 'round' : 'rounds'}.
                  </p>
                )}
              </ClubCard>
            </aside>
          </div>
        )}
      </Container>
    </ClubPage>
  )
}
